// Main Hiring Flow Page - Manages the 5-step process (mandato no forma parte del flujo; disponible en /mandato/:code y en Admin)

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHiringData } from '@/hooks/useHiringData';
import { useLanguage } from '@/hooks/useLanguage';
import { hiringService } from '@/services/hiringService';
import { ServiceDetails } from '@/components/ServiceDetails';
import { ConfirmData } from '@/components/ConfirmData';
import { ContractSignature } from '@/components/ContractSignature';
import { PaymentForm } from '@/components/PaymentForm';
import { ContractSuccess } from '@/components/ContractSuccess';
import { ProgressBar } from '@/components/Layout/ProgressBar';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { hiringProgressService } from '@/services/hiringProgressService';
// Dynamic import para PDF generator (pesado, cargar bajo demanda)

type Step = 1 | 2 | 3 | 4 | 5;

export function HiringFlow() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { details, loading, error } = useHiringData(code || '');
  const { t } = useTranslation('common');
  const { setLanguage } = useLanguage();

  // Set language: ?lang= URL param takes priority over DB value
  useEffect(() => {
    const langParam = searchParams.get('lang');
    if (langParam && ['es', 'en', 'fr'].includes(langParam)) {
      setLanguage(langParam as 'es' | 'en' | 'fr');
    } else if (details?.language) {
      setLanguage(details.language as 'es' | 'en' | 'fr');
    }
  }, [details?.language, setLanguage, searchParams]);
  
  // Normalizar paso: solo 1-5 (enlaces antiguos con step=6 → 5)
  const normalizeStep = (s: number): Step => {
    if (s >= 1 && s <= 5) return s as Step;
    if (s === 6) return 5;
    return 1;
  };
  const stepFromUrl = normalizeStep(parseInt(searchParams.get('step') || '1', 10));
  const stepFromStorage = code ? normalizeStep(parseInt(localStorage.getItem(`hiring_step_${code}`) || '1', 10)) : 1;
  const initialStep = stepFromUrl >= 1 && stepFromUrl <= 5 ? stepFromUrl : stepFromStorage;

  const [currentStep, setCurrentStep] = useState<Step>(initialStep as Step);

  // Sincronizar estado con URL (solo cuando cambia searchParams)
  useEffect(() => {
    const stepFromUrl = normalizeStep(parseInt(searchParams.get('step') || '1', 10));
    if (stepFromUrl >= 1 && stepFromUrl <= 5 && stepFromUrl !== currentStep) {
      setCurrentStep(stepFromUrl as Step);
    }
  }, [searchParams]);

  // Actualizar URL y localStorage cuando cambie el paso
  useEffect(() => {
    const currentStepFromUrl = parseInt(searchParams.get('step') || '1', 10);
    if (currentStep !== currentStepFromUrl) {
      setSearchParams({ step: currentStep.toString() }, { replace: true });
    }
    
    // Persistir paso en localStorage
    if (code) {
      localStorage.setItem(`hiring_step_${code}`, currentStep.toString());
      void hiringProgressService.saveProgress(code, { step: currentStep });
    }
  }, [currentStep, setSearchParams, code]); // Removido searchParams de las dependencias

  // Cargar progreso persistido para retomar flujo y reutilizar enlace.
  useEffect(() => {
    if (!code) return;
    let mounted = true;
    const hydrateProgress = async () => {
      const progress = await hiringProgressService.getProgress(code);
      if (!mounted || !progress) return;
      if (progress.step && progress.step > currentStep) {
        setCurrentStep(progress.step);
        setSearchParams({ step: progress.step.toString() }, { replace: true });
      }
    };
    void hydrateProgress();
    return () => {
      mounted = false;
    };
  }, [code, setSearchParams]);

         // Verificar expiración y guardar detalles en localStorage
         useEffect(() => {
           if (details) {
             const expirationDate = new Date(details.expires_at);
             const now = new Date();
             
             if (now > expirationDate) {
               navigate('/expirado');
             } else {
               // Guardar detalles en localStorage para uso en ContractSuccess
               if (code) {
                 localStorage.setItem(`hiring_details_${code}`, JSON.stringify(details));
               }
             }
           }
         }, [details, navigate, code]);

  // Protección: redirigir según estado del contrato y pago (mandato no forma parte del flujo de contratación)
  useEffect(() => {
    if (!details || !code) return;

    const isContractAccepted = details.contract_accepted === true;
    const isPaid = details.status === 'paid' || details.status === 'completed';

    // Si ya está firmado Y pagado, redirigir al paso 5 (Finalizado)
    if (isContractAccepted && isPaid && currentStep !== 5) {
      setCurrentStep(5);
      setSearchParams({ step: '5' }, { replace: true });
      return;
    }

    // Si el contrato está firmado y el usuario está en pasos 1 o 2, llevar al paso 4 (Pago)
    if (isContractAccepted && (currentStep === 1 || currentStep === 2)) {
      setCurrentStep(4);
      setSearchParams({ step: '4' }, { replace: true });
    }
  }, [details, currentStep, setSearchParams, code]);

  // Detectar retorno de Stripe Checkout y subir contrato definitivo
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentIntent = searchParams.get('payment_intent');
    
    // Si viene de Stripe Checkout (con session_id o payment_intent)
    if ((sessionId || paymentIntent) && code && details) {
      const uploadedFlag = localStorage.getItem(`contract_uploaded_${code}`);
      
      // Evitar subir múltiples veces
      if (uploadedFlag === 'true') {
        console.log('✅ Contrato definitivo ya fue subido anteriormente');
        setCurrentStep(5);
        setSearchParams({ step: '5' }, { replace: true });
        return;
      }
      
      console.log('🔄 Retorno de Stripe detectado, subiendo contrato definitivo...');
      
      const uploadFinalContract = async () => {
        try {
          // Obtener firma del cliente (texto legacy)
          const clientSignature = localStorage.getItem(`client_signature_${code}`);
          
          // Obtener firmas manuscritas (base64 images) con fallback al backend
          const handwrittenSignatures = await hiringProgressService.getHandwrittenSignatures(code);
          
          // Obtener payment_intent del query param o usar sessionId
          const paymentIntentId = paymentIntent || sessionId || 'stripe_checkout';
          await hiringProgressService.saveProgress(code, {
            step: 5,
            payment: {
              status: 'paid',
              session_id: sessionId || undefined,
              payment_intent_id: paymentIntentId,
              updated_at: new Date().toISOString(),
            },
          });
          
          // Dynamic import para PDF generator
          const { generateContractPDF } = await import('@/utils/contractPdfGenerator');
          // Generar PDF definitivo CON firma (isDraft = false)
          // Cargar anexos si existen
          let annexesToInclude: Array<{ title: string; content: string }> | undefined = undefined;
          try {
            const loadedAnnexes = await hiringService.getAnnexes(code);
            if (loadedAnnexes.length > 0) {
              annexesToInclude = loadedAnnexes.map(a => ({ title: a.title, content: a.content }));
              console.log('📎 Anexos cargados para PDF definitivo:', annexesToInclude.length);
            }
          } catch (annexError) {
            console.warn('No se pudieron cargar anexos:', annexError);
          }
          
          const { getMigroSignatureFromStorage } = await import('@/config/migroSignatureStorage');
          const contractBlob = generateContractPDF(details, {
            paymentIntentId,
            stripeTransactionId: paymentIntentId,
            paymentDate: new Date().toISOString(),
            paymentMethod: 'Stripe Checkout',
            clientSignature: clientSignature || undefined,
            handwrittenSignatures,
            migroSignatureImage: getMigroSignatureFromStorage() ?? undefined,
          }, false, annexesToInclude, { language: effectiveDetails?.language || 'es' }); // isDraft = false, con idioma del contrato
          
          console.log('📄 Contrato definitivo generado con firma, tamaño:', contractBlob.size);
          
          // Preparar FormData para subir al backend
          const formData = new FormData();
          formData.append('contract', contractBlob, `contrato_definitivo_${code}.pdf`);
          formData.append('hiring_code', code);
          formData.append('client_email', details.client_email || '');
          formData.append('client_name', details.client_name || '');
          formData.append('contract_type', 'final');
          formData.append('payment_intent_id', paymentIntentId);
          formData.append('stripe_transaction_id', paymentIntentId);
          formData.append('payment_date', new Date().toISOString());
          formData.append('payment_method', 'Stripe Checkout');
          
          // Subir contrato definitivo al backend
          await hiringService.uploadFinalContract(formData);
          console.log('✅ Contrato definitivo subido exitosamente al backend');
          
          // Marcar como subido
          localStorage.setItem(`contract_uploaded_${code}`, 'true');
          await hiringProgressService.saveProgress(code, {
            step: 5,
            payment: {
              status: 'completed',
              session_id: sessionId || undefined,
              payment_intent_id: paymentIntentId,
              updated_at: new Date().toISOString(),
            },
          });
          setCurrentStep(5);
          setSearchParams({ step: '5' }, { replace: true });
        } catch (error) {
          console.error('❌ Error subiendo contrato definitivo:', error);
          setCurrentStep(5);
          setSearchParams({ step: '5' }, { replace: true });
        }
      };
      
      uploadFinalContract();
    }
  }, [searchParams, code, details, setSearchParams]);

  // Handlers para navegación entre pasos
  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep((prev) => (prev + 1) as Step);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as Step);
    }
  };

  // Handler para confirmar datos (Step 2)
  const handleConfirmData = async () => {
    if (!code) return;

    await hiringService.confirmData(code, {
      confirmed: true,
    });
    
    handleNext();
  };

  // Handler para completar firma del contrato (Step 3) → ir a paso 4 (Pago)
  const handleSignatureComplete = (signature?: string, allSignatures?: Record<string, string>) => {
    if (code) {
      // Guardar nombre como firma legacy (compatibilidad)
      if (signature) {
        localStorage.setItem(`client_signature_${code}`, signature);
      }
      // Guardar firmas manuscritas base64 (nuevas)
      if (allSignatures) {
        localStorage.setItem(`contract_signatures_${code}`, JSON.stringify(allSignatures));
      }
    }
    handleNext();
  };

  // Handler para completar pago (Step 4)
  const handlePaymentSuccess = () => {
    handleNext();
  };

  // Limpiar estado al llegar al paso 5 (Finalizado)
  useEffect(() => {
    if (currentStep === 5 && code) {
      localStorage.removeItem(`hiring_step_${code}`);
      console.log('Proceso completado - Estado limpiado');
    }
  }, [currentStep, code]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="mx-auto mb-4 animate-spin text-primary" width={48} height={48} />
          <p className="text-gray-600">{t('buttons.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Error</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold"
          >
            {t('home.cta.continue')}
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!details || !code) {
    return null;
  }

  // Apply ?lang= URL override to details so all child components see the right language
  const langParam = searchParams.get('lang');
  const effectiveDetails = langParam && ['es', 'en', 'fr'].includes(langParam)
    ? { ...details, language: langParam as 'es' | 'en' | 'fr' }
    : details;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto py-4 px-4">
          <ProgressBar currentStep={currentStep} />
        </div>
      </div>

      {/* Step Content */}
      <div className="py-8">
        {currentStep === 1 && (
          <ServiceDetails
            details={effectiveDetails}
            onNext={handleNext}
          />
        )}

        {currentStep === 2 && (
          <ConfirmData
            details={effectiveDetails}
            onConfirm={handleConfirmData}
            onBack={handleBack}
          />
        )}

        {currentStep === 3 && (
          <ContractSignature
            hiringCode={code}
            userName={effectiveDetails.client_name}
            isAlreadySigned={effectiveDetails.contract_accepted === true}
            details={effectiveDetails}
            onComplete={handleSignatureComplete}
            onBack={handleBack}
          />
        )}

        {currentStep === 4 && (
          <PaymentForm
            hiringCode={code}
            amount={effectiveDetails.amount}
            currency={effectiveDetails.currency}
            serviceName={effectiveDetails.service_name}
            hiringDetails={effectiveDetails}
            onSuccess={handlePaymentSuccess}
            onBack={handleBack}
          />
        )}

        {currentStep === 5 && (
          <ContractSuccess
            hiringCode={code}
            serviceName={details.service_name}
            userEmail={details.client_email}
          />
        )}
      </div>
    </div>
  );
}

